import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@tonic/shared/components/ui';
import { DashboardChart } from '@tonic/shared/components/ui';
import {
  Download,
  DollarSign,
  Package2,
  Users,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Calendar
} from 'lucide-react';
import { mockRentals } from './mock-data';

// Mock data for rental analytics
const mockAnalytics = {
  rentalsByStatus: [
    { name: 'Active', value: 145 },
    { name: 'Pending', value: 64 },
    { name: 'Completed', value: 32 }
  ],
  rentalsByCategory: [
    { name: 'Bedroom', value: 89 },
    { name: 'Living Room', value: 45 },
    { name: 'Storage', value: 67 },
    { name: 'Office', value: 40 }
  ],
  monthlyTrends: [
    { date: '2024-01', rentals: 42, revenue: 12500 },
    { date: '2024-02', rentals: 58, revenue: 15800 },
    { date: '2024-03', rentals: 75, revenue: 19200 }
  ],
  customerSatisfaction: [
    { date: '2024-01', rating: 4.5 },
    { date: '2024-02', rating: 4.7 },
    { date: '2024-03', rating: 4.8 }
  ],
  rentalDuration: [
    { range: '1-7 days', count: 45 },
    { range: '8-30 days', count: 78 },
    { range: '31-90 days', count: 34 },
    { range: '90+ days', count: 12 }
  ]
};

const timeRanges = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: '12m', label: 'Last 12 Months' },
];

const metrics = [
  {
    title: 'Total Revenue',
    value: '$45,250',
    change: '+15.3%',
    trend: 'up',
    icon: DollarSign,
  },
  {
    title: 'Active Rentals',
    value: '89',
    change: '+4.5%',
    trend: 'up',
    icon: Package2,
  },
  {
    title: 'Active Customers',
    value: '156',
    change: '+12.8%',
    trend: 'up',
    icon: Users,
  },
  {
    title: 'Avg. Rental Duration',
    value: '45 days',
    change: '-2.3%',
    trend: 'down',
    icon: Clock,
  },
];

const revenueData = [
  { date: '2024-01-01', value: 32500 },
  { date: '2024-02-01', value: 35600 },
  { date: '2024-03-01', value: 38900 },
  { date: '2024-04-01', value: 42100 },
  { date: '2024-05-01', value: 45250 },
];

const popularItems = [
  { name: 'Office Desk', count: 45 },
  { name: 'Conference Table', count: 38 },
  { name: 'Ergonomic Chair', count: 32 },
  { name: 'Filing Cabinet', count: 28 },
  { name: 'Monitor Stand', count: 25 },
];

const demographics = [
  { group: 'Corporate', percentage: 45 },
  { group: 'Small Business', percentage: 30 },
  { group: 'Individual', percentage: 15 },
  { group: 'Event', percentage: 10 },
];

const durationData = [
  { duration: '1-7 days', count: 25 },
  { duration: '8-30 days', count: 45 },
  { duration: '31-90 days', count: 20 },
  { duration: '90+ days', count: 10 },
];

export function RentalAnalyticsPage() {
  const [timeRange, setTimeRange] = React.useState('30d');

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Analytics</h1>
          <p className="text-sm text-muted-foreground">
            Track rental performance and metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-muted-foreground" />
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timeRanges.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric) => (
          <Card key={metric.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {metric.title}
              </CardTitle>
              <metric.icon className="h-4 w-4 text-neutral-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metric.value}</div>
              <div className="flex items-center space-x-2">
                <Badge variant={metric.trend === 'up' ? 'default' : 'destructive'}>
                  {metric.trend === 'up' ? (
                    <ArrowUpRight className="w-4 h-4" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4" />
                  )}
                  {metric.change}
                </Badge>
                <span className="text-sm text-neutral-500">vs. last period</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Revenue Trend"
              data={revenueData}
              type="line"
              dataKey="value"
              xAxisKey="date"
              height={300}
              colors={['#10B981']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Popular Items</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Most Rented Items"
              data={popularItems}
              type="bar"
              dataKey="count"
              xAxisKey="name"
              height={300}
              colors={['#6366F1']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Customer Demographics</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Customer Types"
              data={demographics}
              type="pie"
              dataKey="percentage"
              xAxisKey="group"
              height={300}
              colors={['#10B981', '#6366F1', '#F59E0B', '#3B82F6']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Rental Duration Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Duration Distribution"
              data={durationData}
              type="bar"
              dataKey="count"
              xAxisKey="duration"
              height={300}
              colors={['#6366F1']}
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Rentals by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Rental Status Distribution"
              type="pie"
              data={mockAnalytics.rentalsByStatus}
              dataKey="value"
              xAxisKey="name"
              height={300}
              colors={['#10B981', '#6366F1', '#F59E0B']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Rentals by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Category Distribution"
              type="pie"
              data={mockAnalytics.rentalsByCategory}
              dataKey="value"
              xAxisKey="name"
              height={300}
              colors={['#6366F1', '#10B981', '#F59E0B', '#3B82F6']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Monthly Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Rental and Revenue Trends"
              type="line"
              data={mockAnalytics.monthlyTrends}
              dataKey="rentals"
              xAxisKey="date"
              height={300}
              colors={['#6366F1']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Customer Satisfaction</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Average Rating Over Time"
              type="line"
              data={mockAnalytics.customerSatisfaction}
              dataKey="rating"
              xAxisKey="date"
              height={300}
              colors={['#10B981']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Rental Duration</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Duration Distribution"
              type="bar"
              data={mockAnalytics.rentalDuration}
              dataKey="count"
              xAxisKey="range"
              height={300}
              colors={['#6366F1']}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 
